import { FunctionComponent, useCallback, useMemo } from 'react'

import { useQuery } from '@tanstack/react-query'

import config from '@config'
import {
  getAureusBatchRecommendations,
  GetAureusBatchRecommendationsResponse,
  getAureusPurpose,
  triggerAureusDeboosting,
} from '@utils/aureus'
import useHasAureusJsClientScriptInitialized from '@hooks/useHasAureusJsClientScriptInitialized'
import usePageMetadata from '@hooks/usePageMetadata'
import useTracking, { TrackingFnType } from '@hooks/useTracking'
import { JSONWidgetType as TeaserStandardVerticalJSONWidgetType } from '@widgets/TeaserStandardVertical'
import { ToplistAPIProps } from '@widgets/Toplist'
import Wrapper from '@components/Toplist/Wrapper'

import Story from './Toplist/Story'

type MostReadProps = Pick<ToplistAPIProps, 'title' | 'hideOnDesktop'>

interface AureusMostReadItem {
  aureusOfferId: string
  articleID: string
  teasers: {
    standardVertical: TeaserStandardVerticalJSONWidgetType
  }
}

type MostReadItem = TeaserStandardVerticalJSONWidgetType & {
  articleId: string
  aureusOfferId: string
  order: number
}

type GetTopListAureus = (
  articleId: string
) => GetAureusBatchRecommendationsResponse<AureusMostReadItem>

const {
  publication: { publication },
  backend: { deploymentEnv },
} = config

const getTopListAureus: GetTopListAureus = (articleId: string) =>
  getAureusBatchRecommendations<AureusMostReadItem>({
    'polaris-popular': {
      resultsLimit: 3,
      context: {
        section: `polaris${publication === 'romandie' ? '-romandie' : ''}-popular-${deploymentEnv === 'prod' ? 'prod' : 'stg'}`,
        purpose: getAureusPurpose(),
        contentBlacklist: articleId,
      },
    },
  } as const)

const getMostRead = async (id: string) => {
  let responseData: MostReadItem[] = []

  try {
    const queryResult = await getTopListAureus(id)

    responseData =
      queryResult?.results?.['polaris-popular']?.recommendations?.reduce(
        (acc, curr, index) => {
          acc.push({
            ...curr.teasers.standardVertical,
            articleId: curr.articleID,
            aureusOfferId: curr.aureusOfferId,
            order: index + 1,
          } satisfies MostReadItem)

          return acc
        },
        [] as MostReadItem[]
      ) ?? []
  } catch {}

  return responseData
}

const MostReadStory: FunctionComponent<MostReadItem> = ({
  articleId,
  title,
  catchword,
  link,
  isPlus,
  aureusOfferId,
  order,
}) => {
  const onClick = useCallback(
    () => ({
      event: 'select_content',
      placement: 'article-recommendations-most-read',
      link_id: link?.href,
    }),
    [link?.href]
  )

  const trackOnClick = useTracking(onClick)

  const onClickWithDeboosting = useCallback(() => {
    triggerAureusDeboosting({
      itemsToDeboost: [{ offerId: aureusOfferId, contentId: articleId }],
    })

    trackOnClick()
  }, [articleId, aureusOfferId, trackOnClick])

  return (
    <Story
      key={articleId}
      order={order}
      title={title}
      catchword={catchword}
      articleId={articleId}
      link={link}
      isPlus={isPlus}
      aureusOfferId={aureusOfferId}
      onClick={onClickWithDeboosting}
    />
  )
}

const MostRead: FunctionComponent<MostReadProps> = ({
  title,
  hideOnDesktop,
}) => {
  const pageMetadata = usePageMetadata()

  const id = pageMetadata.id

  const hasAureusJsClientScriptInitialized =
    useHasAureusJsClientScriptInitialized()

  const { data: mostReadItems } = useQuery<MostReadItem[]>({
    queryKey: ['most-read', id],
    queryFn: () => getMostRead(id as string),
    enabled: !!id && hasAureusJsClientScriptInitialized,
    gcTime: 0,
  })

  const renderedMostReadItems = useMemo(
    () =>
      mostReadItems ? (
        <>
          {mostReadItems.map((mostReadItem) => (
            <MostReadStory key={mostReadItem.articleId} {...mostReadItem} />
          ))}
        </>
      ) : null,
    [mostReadItems]
  )

  const onImpressionTracking = useCallback<TrackingFnType>(
    () => ({
      event: 'content_impression',
      placement: 'article-recommendations-most-read',
      link_id: pageMetadata.url,
    }),
    [pageMetadata.url]
  )

  const onImpression = useTracking(onImpressionTracking)

  return (
    <Wrapper
      title={title}
      hideOnDesktop={hideOnDesktop}
      onImpression={onImpression}>
      {renderedMostReadItems}
    </Wrapper>
  )
}

export default MostRead
